import EditIcon from '@mui/icons-material/Edit';
import LoginIcon from '@mui/icons-material/Login';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import RingVolumeIcon from '@mui/icons-material/RingVolume';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { SvgIconTypeMap } from '@mui/material/SvgIcon';
import React from 'react';

import AgentStatusBarBase, { StatusColor } from '~components/AgentStatusBarBase';
import { AgentState } from '~services/AsteriskDiallerManager/domain';

type Props = {
  statusList: string[];
  status: string;
  statusTimestamp: string;
  pendingStatus?: string;
  campaign: string;
  setAgentState: (status: string) => void;
  onViewCall?: () => void;
  onCallPreview?: () => void;
  showViewCall?: boolean;
  showCallPreview?: boolean;
  manualOutboundOpen?: boolean;
};

type StatusBadge = {
  backgroundColor: StatusColor;
  statusName: string;
  statusIcon: OverridableComponent<SvgIconTypeMap<{}, 'svg'>>;
};

const statusBadgeData: { [key in AgentState]: StatusBadge } = {
  [AgentState.LoggedIn]: {
    backgroundColor: StatusColor.blue,
    statusName: 'Connecting...',
    statusIcon: LoginIcon,
  },
  [AgentState.Initialised]: {
    backgroundColor: StatusColor.blueGrey,
    statusName: 'Initialised',
    statusIcon: PhoneDisabledIcon,
  },
  [AgentState.CheckingAssignments]: {
    backgroundColor: StatusColor.blueGrey,
    statusName: 'Checking Assignments',
    statusIcon: PhoneDisabledIcon,
  },
  [AgentState.Disconnected]: {
    backgroundColor: StatusColor.blueGrey,
    statusName: 'Disconnected',
    statusIcon: PhoneDisabledIcon,
  },
  [AgentState.Unavailable]: {
    backgroundColor: StatusColor.blueGrey,
    statusName: 'Not Ready',
    statusIcon: PhoneDisabledIcon,
  },
  [AgentState.InQueue]: {
    backgroundColor: StatusColor.green,
    statusName: 'Available',
    statusIcon: PhoneIcon,
  },
  [AgentState.Preview]: {
    backgroundColor: StatusColor.green,
    statusName: 'Preview',
    statusIcon: PhoneIcon,
  },
  [AgentState.Ringing]: {
    backgroundColor: StatusColor.green,
    statusName: 'Ringing',
    statusIcon: RingVolumeIcon,
  },
  [AgentState.Connected]: {
    backgroundColor: StatusColor.blue,
    statusName: 'Connected',
    statusIcon: PhoneInTalkIcon,
  },
  [AgentState.AfterCallWork]: {
    backgroundColor: StatusColor.grey,
    statusName: 'After Call Work',
    statusIcon: EditIcon,
  },
};

export const StatusBar = ({
  statusList,
  status,
  statusTimestamp,
  pendingStatus,
  campaign,
  setAgentState,
  onViewCall,
  onCallPreview,
  showViewCall,
  showCallPreview,
  manualOutboundOpen,
}: Props) => {
  const s = statusBadgeData[status as AgentState] || {
    backgroundColor: StatusColor.blueGrey,
    statusName: status,
    statusIcon: PhoneDisabledIcon,
  };
  const disabled =
    manualOutboundOpen ||
    (status !== AgentState.InQueue && status !== AgentState.Unavailable && statusList.indexOf(status) === -1);

  const actionButtons = (
    <>
      {showViewCall && (
        <Button size='small' variant='outlined' onClick={onViewCall} sx={{ color: '#fff', borderColor: '#fff', mr: 1 }}>
          View Call
        </Button>
      )}
      {showCallPreview && (
        <Button
          size='small'
          variant='contained'
          onClick={onCallPreview}
          sx={{
            'backgroundColor': 'rgba(255, 255, 255, .20)',
            'color': '#fff',
            'fontWeight': 700,
            'textTransform': 'none',
            ':hover': { backgroundColor: 'rgba(255, 255, 255, .40)' },
            'mr': 1,
          }}>
          Call
        </Button>
      )}
    </>
  );

  return (
    <AgentStatusBarBase
      statusList={statusList}
      status={s.statusName}
      statusIcon={s.statusIcon}
      statusTimestamp={statusTimestamp}
      statusColor={s.backgroundColor}
      setAgentState={setAgentState}
      isStateChangeDisabled={disabled}
      actionsSection={actionButtons}
      statsSection={
        <small>
          <small>
            <strong>{campaign}</strong>
          </small>
        </small>
      }
      replaceStatusSelector={pendingStatus ? <Box sx={{ color: 'white' }}>Pending: {pendingStatus}</Box> : undefined}
    />
  );
};

export default StatusBar;
